import React, { useEffect, useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { DollarSign, Calendar, Star, Plus, Sparkles, TrendingUp, FileText, ExternalLink } from "lucide-react";
import AIExperienceAssistant from "../components/AIExperienceAssistant";
import ConciergeAwards from "../components/ConciergeAwards";
import BookingSettings from "../components/BookingSettings";
import IdentityVerification from "../components/IdentityVerification";
import EliteUpgrade from "../components/EliteUpgrade";
import TrustBadges from "../components/TrustBadges";
import ConciergeAvailabilityPanel from "../components/ConciergeAvailabilityPanel";
import ExpandableBookingList from "../components/ExpandableBookingList";
import EmptyState from "../components/EmptyState";
import FilterPills, { applyBookingFilters, computeBookingFilterCounts } from "../components/FilterPills";
import CustomOfferModal from "../components/CustomOfferModal";
import BusinessProfilePanel from "../components/BusinessProfilePanel";
import NewBriefRequests from "../components/NewBriefRequests";
import ServiceScopeWidget from "../components/ServiceScopeWidget";
import ConciergeReachMeter from "../components/ConciergeReachMeter";

export default function ProviderDashboard(){
  const { user } = useAuth();
  const [provider, setProvider] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [offerBooking, setOfferBooking] = useState(null);
  const [form, setForm] = useState({ title:"", description:"", category:"concierge", moment_type:"luxury", price:200, duration:"3 hours", city:"Miami", state:"FL", images:[""] });
  const [statusFilter, setStatusFilter] = useState("all");
  const [rangeFilter, setRangeFilter] = useState("all");

  const load = useCallback(() => {
    api.get("/providers/me").then(r=>setProvider(r.data)).catch(()=>setProvider({}));
    api.get("/bookings").then(r=>setBookings(Array.isArray(r.data) ? r.data : [])).catch(()=>setBookings([]));
  }, []);
  useEffect(()=>{ load(); },[load]);

  const isBusiness = provider?.account_type === "business";
  // Account-type-aware labels — same component tree, copy adapts.
  const labels = isBusiness
    ? {
        header: "Experience Requests",
        emptyTitle: "Open your availability to start receiving requests.",
        emptySubtitle: "Once your weekly schedule is live, customers can request business experiences. Open your Availability panel below.",
        earnings: "Business earnings",
        bookingsCount: "Experiences",
      }
    : {
        header: "Booking requests",
        emptyTitle: "Set your availability to start receiving requests.",
        emptySubtitle: "Once your weekly schedule is live, real customers can book you. Open your Availability panel below to get started.",
        earnings: "Booking payout",
        bookingsCount: "Bookings",
      };

  const filteredBookings = useMemo(
    () => applyBookingFilters(bookings, { status: statusFilter, range: rangeFilter }),
    [bookings, statusFilter, rangeFilter]
  );
  const { statusCounts: bkStatusCounts, rangeCounts: bkRangeCounts } = useMemo(
    () => computeBookingFilterCounts(bookings),
    [bookings]
  );

  const verify = async () => { await api.post("/providers/verify/pay"); load(); };
  const accept = async (id) => { await api.post(`/bookings/${id}/accept`); load(); };
  const complete = async (id) => { await api.post(`/bookings/${id}/complete`); load(); };
  const subscribePlus = async () => { await api.post("/providers/trel-plus/subscribe"); load(); };
  const cancelPlus = async () => { await api.post("/providers/trel-plus/cancel"); load(); };
  const createExp = async () => {
    await api.post("/experiences", { ...form, price: +form.price, images: [form.images[0]||"https://images.unsplash.com/photo-1571003123894-1f0594d2b5d9?w=1200"] });
    setShowCreate(false); load();
  };

  if (!provider) return <div className="p-20 text-center">Loading account…</div>;
  if (!provider.user_id) {
    return (
      <div className="max-w-md mx-auto p-20 text-center">
        <h2 className="font-serif text-2xl">Account not ready</h2>
        <p className="text-sm text-[#071A2D]/60 mt-2">We couldn&apos;t load your Concierge profile. Please refresh, or contact support if this continues.</p>
        <button onClick={load} className="btn-outline-emerald mt-6">Retry</button>
      </div>
    );
  }

  // Plan presentation (avoids nested ternaries in JSX)
  const isPlus = provider.trel_plus_active;
  const isApproved = provider.verification_status === "Approved";
  let planName = "Unverified";
  let planSub = "Complete verification to start earning";
  if (isPlus) {
    planName = "TREL+ Concierge";
    planSub = "$29 / month · Premium growth membership";
  } else if (isApproved) {
    planName = "Verified Concierge";
    planSub = "$0 / month · Free tier";
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-10 pb-28 lg:pb-10">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-serif text-4xl">{provider.business_name}</h1>
          <div className="text-sm text-[#071A2D]/60 mt-1 flex items-center gap-2 flex-wrap">
            {isBusiness && (
              <span
                data-testid="dashboard-business-pill"
                className="text-[10px] uppercase tracking-[0.18em] font-semibold px-2 py-0.5 rounded-md bg-[#064E3B]/8 text-[#064E3B]"
              >
                Business · Concierge
              </span>
            )}
            <span>{provider.city}, {provider.state}</span>
            <TrustBadges provider={provider} size="sm"/>
            {provider.verification_status !== "Approved" && (
              <span className="text-xs text-[#FF7A45]">{provider.verification_status}</span>
            )}
          </div>
        </div>
        {provider.verification_status !== "Approved" && (
          <button data-testid="verify-pay" onClick={verify} className="btn-coral">Pay $60 verification fee</button>
        )}
        {/* View Public Profile — Concierges can preview exactly what customers
            see on their /concierge/{slug} page. Different label for business
            accounts ("View Business Profile") to match the spec. Slug uses the
            provider's `slug` field when available, falling back to a slugified
            display name so newly-onboarded concierges still get a working link. */}
        {(() => {
          const slug = provider.slug || slugify(provider.business_name || user?.name || "concierge");
          return (
            <Link
              to={`/concierge/${slug}`}
              data-testid="view-public-profile"
              className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-full bg-white border border-[#C9A45C]/40 text-[#064E3B] font-semibold hover:border-[#064E3B] hover:bg-[#FFF8EE] transition"
            >
              <ExternalLink size={12}/>
              {isBusiness ? "View Business Profile" : "View Public Profile"}
            </Link>
          );
        })()}
      </div>

      {/* Concierge Reach meter — one-line factual narration of the current
          service-scope flags. No fake match estimates or numeric promises —
          just guidance about which kinds of help are enabled and how to
          broaden them. Clicking the CTA scrolls to the ServiceScopeWidget. */}
      <ConciergeReachMeter provider={provider} />

      {/* 0. NEW REQUESTS — Brief Marketplace (Phase 46). Sits ABOVE booking
            requests because these are inbound brief opportunities that
            require a quick Send Offer / Pass decision. Eligibility is
            server-computed via /api/briefs. */}
      <div id="brief-marketplace" className="mt-8 scroll-mt-24">
        <NewBriefRequests />
      </div>

      {/* 0b. Service-scope widget — inline 30-second profile-completion gesture
            that drives the public-profile pills. Hidden for non-providers
            (parent gates the entire dashboard by role) and rendered
            read-only for preview accounts inside the component. */}
      {provider && (
        <div className="mt-8">
          <ServiceScopeWidget provider={provider} onSaved={load} />
        </div>
      )}

      {/* 1. ACTION REQUIRED — Booking requests sit at the very top so the
            Concierge sees what needs their attention before anything else. */}
      <div className="mt-8 flex items-end justify-between">
        <h2 className="font-serif text-2xl">{labels.header}</h2>
        <button data-testid="create-exp" onClick={()=>setShowCreate(true)} className="btn-emerald flex items-center gap-2"><Plus size={16}/> New experience</button>
      </div>
      <div className="mt-4">
        <FilterPills
          testid="provider-bookings-filters"
          status={statusFilter}
          range={rangeFilter}
          onStatusChange={setStatusFilter}
          onRangeChange={setRangeFilter}
          statusOptions={["all", "pending", "confirmed", "completed"]}
          statusCounts={bkStatusCounts}
          rangeCounts={bkRangeCounts}
          className="mb-4"
        />
        <ExpandableBookingList
          items={filteredBookings}
          limit={3}
          testid="provider-booking-requests"
          emptyState={
            <EmptyState
              testid="provider-empty-state"
              title={labels.emptyTitle}
              subtitle={labels.emptySubtitle}
              cta={{ to: "#availability", label: "Open Availability →", testid: "provider-empty-cta" }}
              tone="luxury"
            />
          }
          // Newest requests first. Falls back to booking_id sort if created_at missing.
          sortBy={(a, b) => {
            const ad = new Date(a.created_at || 0).getTime();
            const bd = new Date(b.created_at || 0).getTime();
            return bd - ad;
          }}
          renderItem={(b) => (
            <div key={b.booking_id} className="p-5 rounded-2xl card-luxury flex items-center gap-5 flex-wrap">
              <img src={b.experience?.images?.[0]} className="w-16 h-16 rounded-xl object-cover bg-[#071A2D]/5" alt=""/>
              <div className="flex-1 min-w-[180px]">
                <div className="font-medium">{b.experience?.title}</div>
                <div className="text-sm text-[#071A2D]/60 mt-0.5">
                  {b.customer?.name || "Guest"}
                  {b.experience?.city ? ` · ${b.experience.city}${b.experience.state ? `, ${b.experience.state}` : ""}` : ""}
                </div>
                <div className="text-xs text-[#071A2D]/55 mt-0.5">
                  {b.date} · {b.time || ""} · {b.guests} {b.guests === 1 ? "guest" : "guests"}
                  {b.occasion ? ` · ${b.occasion}` : ""}
                </div>
              </div>
              <div className="text-right">
                <div className="font-semibold">${Number((b.provider_payout || 0) + (b.tip || 0)).toLocaleString()}</div>
                <div className="text-xs">payout (80%){b.tip ? ` + $${b.tip} tip` : ""}</div>
                <div className="text-[10px] px-2 py-1 rounded-full bg-[#064E3B] text-[#FFF8EE] inline-block mt-1">{b.status}</div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setOfferBooking(b)}
                  data-testid={`offer-create-${b.booking_id}`}
                  className="btn-outline-emerald text-xs inline-flex items-center gap-1.5"
                  title="Send a Custom Offer for this request"
                >
                  <FileText size={12}/> Create Offer
                </button>
                {b.status === "Pending Acceptance" && <button onClick={()=>accept(b.booking_id)} className="btn-coral text-sm">Accept</button>}
                {b.status === "Confirmed" && <button onClick={()=>complete(b.booking_id)} className="btn-emerald text-sm">Complete</button>}
              </div>
            </div>
          )}
        />
      </div>

      {/* 2. AVAILABILITY — second priority. Anchor target so empty-state CTA can jump here. */}
      <div id="availability" className="mt-12 scroll-mt-24">
        <ConciergeAvailabilityPanel/>
      </div>

      {/* 3. EARNINGS SUMMARY — compact stat row */}
      <div className="grid md:grid-cols-4 gap-4 mt-12">
        <Stat icon={DollarSign} label={labels.earnings} value={`$${provider.earnings}`}/>
        <Stat icon={DollarSign} label="Tips received" value={`$${(provider.tips_received || 0).toFixed(0)}`}/>
        <Stat icon={Star} label="Rating" value={provider.rating}/>
        <Stat icon={Calendar} label={labels.bookingsCount} value={bookings.length}/>
      </div>

      {/* 4. EXPERIENCE PROPOSALS — TREL+ AI Business Assistant.
          id="ai-assistant" matches the deep-link target from the mobile
          "AI Tools" coral FAB in MobileBottomNav. scroll-mt offsets the
          sticky header so the section lands cleanly in view. */}
      <div id="ai-assistant" className="scroll-mt-24">
        <AIExperienceAssistant provider={provider} onUpgrade={subscribePlus}/>
      </div>

      {/* 5. MEMBERSHIP / TREL+ — kept visible but lower in the flow */}
      <div className="mt-10 rounded-3xl border border-[#071A2D]/10 overflow-hidden shadow-luxury">
        <div className="grid md:grid-cols-2">
          <div className={`p-7 ${provider.trel_plus_active ? "bg-[#064E3B] text-[#FFF8EE]" : "bg-[#FFF8EE]"}`}>
            <div className={`text-[11px] uppercase tracking-[0.22em] ${provider.trel_plus_active ? "text-[#C9A45C]" : "text-[#064E3B]"}`}>Current Plan</div>
            <h3 className={`font-serif text-3xl mt-2 ${provider.trel_plus_active ? "text-[#FFF8EE]" : ""}`}>{planName}</h3>
            <div className={`text-sm mt-1 ${isPlus ? "opacity-80" : "text-[#071A2D]/70"}`}>
              {planSub}
            </div>
            {provider.trel_plus_active && provider.subscription_end_date && (
              <div className={`text-xs mt-3 ${provider.trel_plus_active ? "opacity-60" : "text-[#071A2D]/50"}`}>
                Next billing · {new Date(provider.subscription_end_date).toLocaleDateString()}
              </div>
            )}
            <div className="mt-6 flex gap-3">
              {!provider.trel_plus_active && provider.verification_status === "Approved" && (
                <button data-testid="trel-plus-subscribe" onClick={subscribePlus} className="btn-coral">Upgrade to TREL+ — $29/mo</button>
              )}
              {provider.trel_plus_active && (
                <button data-testid="trel-plus-cancel" onClick={cancelPlus} className="btn-outline-emerald" style={{borderColor:"#FFF8EE", color:"#FFF8EE"}}>Cancel subscription</button>
              )}
            </div>
          </div>
          <div className="p-7 bg-[#FFF8EE]">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#C9A45C]">TREL+ Unlocks</div>
            <ul className="mt-3 space-y-2 text-sm text-[#071A2D]/80">
              <li className="flex items-start gap-2"><TrendingUp size={14} className="mt-1 text-[#C9A45C]"/> Enhanced visibility &amp; AI Concierge matching boost</li>
              <li className="flex items-start gap-2"><Sparkles size={14} className="mt-1 text-[#C9A45C]"/> Premium gold badge &amp; profile tools (video · gallery)</li>
              <li className="flex items-start gap-2"><Star size={14} className="mt-1 text-[#C9A45C]"/> Host Recommended Provider Network advantage</li>
              <li className="flex items-start gap-2"><DollarSign size={14} className="mt-1 text-[#C9A45C]"/> Advanced analytics — views, conversion, revenue trends</li>
            </ul>
          </div>
        </div>
      </div>

      {/* 6. TRUST & CREDENTIALS */}
      <h2 className="font-serif text-2xl mt-12 mb-4">Trust & Credentials</h2>
      <div className="grid lg:grid-cols-2 gap-5">
        <IdentityVerification provider={provider} onChange={load}/>
        <div className="rounded-3xl card-luxury p-6" data-testid="cert-summary-card">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#064E3B] font-semibold">Trust · Level 2</div>
          <h3 className="font-serif text-2xl mt-2 flex items-center gap-2">
            <Sparkles className="text-[#064E3B]" size={20}/> TREL Certified Concierge
          </h3>
          <p className="text-sm text-[#071A2D]/70 mt-2 max-w-md leading-relaxed">
            Complete the 5-page TREL Certified Training to unlock the emerald badge
            and start accepting bookings. Free, ~10 minutes.
          </p>
          {(provider?.trel_certified || provider?.certification?.completed_at) ? (
            <div className="mt-5 text-xs text-[#064E3B] flex items-center gap-2">
              ✓ TREL Certified · Training complete
            </div>
          ) : (
            <a href="/onboarding/concierge" className="btn-coral inline-flex items-center gap-2 mt-5">
              Start TREL Certification
            </a>
          )}
        </div>
      </div>
      <div className="mt-5">
        <EliteUpgrade provider={provider} onChange={load}/>
      </div>

      {/* 7. AWARDS & MILESTONES */}
      <ConciergeAwards provider={provider}/>

      {/* 8. BUSINESS PROFILE — only for account_type === "business" */}
      {isBusiness && (
        <BusinessProfilePanel key={provider.user_id} provider={provider} onSaved={load}/>
      )}

      {/* 9. PROFILE TOOLS — Booking settings stay at the bottom */}
      <BookingSettings/>

      {/* Custom Offer modal (Phase 30) — concierge sends structured proposal */}
      <CustomOfferModal
        booking={offerBooking}
        open={!!offerBooking}
        onClose={() => setOfferBooking(null)}
        onSent={load}
      />

      {showCreate && (
        <div className="fixed inset-0 bg-[#071A2D]/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#FFF8EE] rounded-3xl p-8 max-w-lg w-full">
            <h3 className="font-serif text-2xl">Create experience</h3>
            <div className="mt-5 space-y-4">
              <input data-testid="exp-title" placeholder="Title" value={form.title} onChange={e=>setForm({...form,title:e.target.value})} className="kbd-input"/>
              <textarea placeholder="Description" rows="3" value={form.description} onChange={e=>setForm({...form,description:e.target.value})} className="kbd-input"/>
              <div className="grid grid-cols-2 gap-4">
                <input placeholder="Price" type="number" value={form.price} onChange={e=>setForm({...form,price:e.target.value})} className="kbd-input"/>
                <input placeholder="Duration" value={form.duration} onChange={e=>setForm({...form,duration:e.target.value})} className="kbd-input"/>
                <input placeholder="City" value={form.city} onChange={e=>setForm({...form,city:e.target.value})} className="kbd-input"/>
                <input placeholder="State" value={form.state} onChange={e=>setForm({...form,state:e.target.value})} className="kbd-input"/>
                <input placeholder="Image URL" value={form.images[0]} onChange={e=>setForm({...form,images:[e.target.value]})} className="kbd-input col-span-2"/>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={createExp} className="btn-coral flex-1">Create</button>
              <button onClick={()=>setShowCreate(false)} className="btn-outline-emerald">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
const Stat = ({icon:I,label,value}) => (
  <div className="p-5 rounded-2xl card-luxury">
    <I className="text-[#064E3B]" size={18}/>
    <div className="text-xs uppercase tracking-widest text-[#071A2D]/60 mt-3">{label}</div>
    <div className="text-2xl font-serif mt-1">{value}</div>
  </div>
);

// Mirror backend `_slugify_name` so the View Public Profile button always
// lands on the same URL the homepage/Explore cards generate. Keeps deep
// links consistent for both seeded and newly-onboarded concierges.
function slugify(name) {
  if (!name) return "concierge";
  return String(name)
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .split("-")
    .filter(Boolean)
    .join("-") || "concierge";
}
